import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Header from '@/components/Header';
import ScoreBar from '@/components/ScoreBar';
import StatusBadge from '@/components/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload, FileText, Trash2, Calendar, Mail, MapPin, X, ExternalLink, Eye, Download, ChevronDown, ChevronRight, Briefcase, Sparkles, Users, Phone, DollarSign, Search, Filter, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Resumes = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [recentOnly, setRecentOnly] = useState(false);
  const [uploadingZip, setUploadingZip] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [expandedSkills, setExpandedSkills] = useState({});
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [resumeContent, setResumeContent] = useState(null);
  const [expandedMatches, setExpandedMatches] = useState({});
  const [loadingMatches, setLoadingMatches] = useState({});
  const [uploadMatchResults, setUploadMatchResults] = useState(null); // New state for upload match popup
  
  // Filter states
  const [searchName, setSearchName] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterMinExp, setFilterMinExp] = useState('');
  const [filterMaxExp, setFilterMaxExp] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchResumes();
  }, [recentOnly]);

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const skills = new Set();
    const locations = new Set();
    
    resumes.forEach(r => {
      r.skills?.forEach(s => skills.add(s));
      if (r.location) locations.add(r.location);
      if (r.current_location) locations.add(r.current_location);
    });
    
    return {
      skills: Array.from(skills).sort(),
      locations: Array.from(locations).sort()
    };
  }, [resumes]);

  // Filtered resumes
  const filteredResumes = useMemo(() => {
    return resumes.filter(resume => {
      // Search by name
      if (searchName && !resume.name?.toLowerCase().includes(searchName.toLowerCase())) {
        return false;
      }
      
      // Filter by skill
      if (filterSkill && !resume.skills?.some(s => s.toLowerCase().includes(filterSkill.toLowerCase()))) {
        return false;
      }
      
      // Filter by location
      if (filterLocation) {
        const resumeLocation = (resume.location || resume.current_location || '').toLowerCase();
        if (!resumeLocation.includes(filterLocation.toLowerCase())) {
          return false;
        }
      }
      
      // Filter by experience range
      const exp = resume.experience_years || 0;
      if (filterMinExp && exp < parseFloat(filterMinExp)) {
        return false;
      }
      if (filterMaxExp && exp > parseFloat(filterMaxExp)) {
        return false;
      }
      
      return true;
    });
  }, [resumes, searchName, filterSkill, filterLocation, filterMinExp, filterMaxExp]);

  const clearFilters = () => {
    setSearchName('');
    setFilterSkill('');
    setFilterLocation('');
    setFilterMinExp('');
    setFilterMaxExp('');
  };

  const hasActiveFilters = searchName || filterSkill || filterLocation || filterMinExp || filterMaxExp;

  const fetchResumes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/resumes?recent_only=${recentOnly}`);
      setResumes(response.data);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      toast.error('Failed to fetch resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      // Use the new endpoint that returns matches immediately
      const response = await axios.post(`${API}/resumes/upload-with-matches`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minute timeout for AI parsing
      });
      
      const { resumes, match_results, total_resumes, total_matches } = response.data;
      
      toast.success(`Successfully uploaded ${total_resumes} resume(s) with ${total_matches} matching job(s)!`);
      
      // Show match results popup if there are matches
      if (match_results && match_results.length > 0 && match_results.some(r => r.matches.length > 0)) {
        setUploadMatchResults(match_results);
      }
      
      fetchResumes();
    } catch (error) {
      console.error('Error uploading resumes:', error);
      toast.error('Failed to upload resumes');
    } finally {
      setUploading(false);
    }
  };

  const handleZipUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (15MB limit)
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 15MB');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please upload a ZIP file');
      return;
    }

    setUploadingZip(true);
    toast.info(`Processing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)... This may take a few minutes.`);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-zip`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000 // 10 minute timeout for large files
      });
      
      const { resumes_uploaded, jds_uploaded, errors, matching_status, note } = response.data;
      
      toast.success(`Processed: ${resumes_uploaded} resume(s), ${jds_uploaded} JD(s)`);
      
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} file(s) had issues: ${errors.slice(0, 2).join(', ')}${errors.length > 2 ? '...' : ''}`);
      }
      
      // Background matching notification
      if (matching_status === 'background_started') {
        toast.info('🔄 Matching is running in the background. Check the Jobs page in a few moments for matches.');
      }
      
      if (note) {
        toast.info(note);
      }
      
      // Refresh the list
      fetchResumes();
      
    } catch (error) {
      console.error('Error uploading ZIP:', error);
      if (error.response?.status === 413) {
        toast.error('File too large. Maximum size is 15MB');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. The file may still be processing. Refresh the page in a minute.');
      } else {
        const detail = error.response?.data?.detail || 'Failed to process ZIP file';
        toast.error(detail);
        console.error('ZIP upload error detail:', error.response?.data);
      }
    } finally {
      setUploadingZip(false);
      event.target.value = ''; // Reset file input
    }
  };

  const autoMatchResumes = async (resumeIds) => {
    try {
      const jobsResponse = await axios.get(`${API}/jobs?status=ACTIVE`);
      if (jobsResponse.data.length === 0) {
        toast.info('No active jobs to match against');
        return;
      }
      
      toast.info('Finding matching jobs...');
      await axios.post(`${API}/match/run`);
      
      // Fetch match results for the uploaded resumes
      const allMatches = [];
      for (const jd of jobsResponse.data) {
        const matchRes = await axios.get(`${API}/match/results/${jd.id}?min_score=50`);
        const resumeMatches = matchRes.data.filter(m => resumeIds.includes(m.resume_id));
        if (resumeMatches.length > 0) {
          allMatches.push({
            job: jd,
            matches: resumeMatches
          });
        }
      }
      
      if (allMatches.length > 0) {
        setMatchResults(allMatches);
        toast.success(`Found ${allMatches.length} matching job(s)!`);
      } else {
        toast.info('No matches found with score ≥ 50%');
      }
    } catch (error) {
      console.error('Error matching resumes:', error);
    }
  };

  const handleDelete = async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return;

    try {
      await axios.delete(`${API}/resumes/${resumeId}`);
      toast.success('Resume deleted successfully');
      fetchResumes();
    } catch (error) {
      console.error('Error deleting resume:', error);
      toast.error('Failed to delete resume');
    }
  };

  const toggleSkills = (resumeId) => {
    setExpandedSkills(prev => ({
      ...prev,
      [resumeId]: !prev[resumeId]
    }));
  };

  const handleViewResume = async (resumeId) => {
    try {
      const response = await axios.get(`${API}/resumes/${resumeId}/raw`);
      setResumeContent(response.data);
      setShowResumeDialog(true);
    } catch (error) {
      console.error('Error fetching resume:', error);
      toast.error('Failed to load resume');
    }
  };

  const handleDownloadResume = (resume) => {
    const element = document.createElement('a');
    const file = new Blob([resume.raw_text || 'No content'], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = resume.filename || `${resume.name}_resume.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Resume downloaded');
  };

  const toggleMatchingJobs = async (resumeId) => {
    if (expandedMatches[resumeId]) {
      setExpandedMatches(prev => ({...prev, [resumeId]: null}));
      return;
    }

    setLoadingMatches(prev => ({...prev, [resumeId]: true}));
    try {
      const response = await axios.get(`${API}/resumes/${resumeId}/matching-jobs?min_score=40`);
      setExpandedMatches(prev => ({...prev, [resumeId]: response.data}));
    } catch (error) {
      console.error('Error fetching matching jobs:', error);
      toast.error('Failed to load matching jobs');
    } finally {
      setLoadingMatches(prev => ({...prev, [resumeId]: false}));
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-brand-cyan/5 to-brand-magenta/5 min-h-screen">
      <Header
        title="Resume Database"
        subtitle={`${resumes.length} resume(s) in database`}
        action={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setRecentOnly(!recentOnly)}
              className={recentOnly ? 'bg-brand-cyan/10 border-brand-cyan text-brand-cyan' : 'border-slate-200'}
              data-testid="filter-recent-button"
            >
              {recentOnly ? 'Show All' : 'Recent Only (≤3 months)'}
            </Button>
            <Button
              variant="outline"
              onClick={() => document.getElementById('zip-upload-resumes').click()}
              disabled={uploadingZip}
              className="border-brand-orange/30 hover:border-brand-orange hover:bg-brand-orange/10"
              data-testid="upload-zip-resumes-button"
            >
              <Upload size={16} className="mr-2 text-brand-orange" />
              {uploadingZip ? 'Processing ZIP...' : 'Upload ZIP'}
            </Button>
            <input
              id="zip-upload-resumes"
              type="file"
              accept=".zip"
              onChange={handleZipUpload}
              className="hidden"
              data-testid="zip-upload-resumes-input"
            />
            <Button
              className="bg-gradient-to-r from-brand-blue to-brand-cyan text-white hover:from-brand-cyan hover:to-brand-blue shadow-lg"
              onClick={() => document.getElementById('file-upload').click()}
              disabled={uploading}
              data-testid="upload-resumes-button"
            >
              <Upload size={16} className="mr-2" />
              {uploading ? 'Uploading...' : 'Upload Resumes'}
            </Button>
            <input
              id="file-upload"
              type="file"
              multiple
              accept=".pdf,.docx,.txt"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="file-upload-input"
            />
          </div>
        }
      />

      <div className="p-8">
        {/* Search and Filter Section */}
        <Card className="bg-white/90 backdrop-blur-sm border-2 border-brand-blue/10 p-4 mb-6 shadow-sm rounded-xl" data-testid="filter-section">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search by Name */}
            <div className="flex-1 min-w-[200px] max-w-[300px]">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  className="pl-9 border-slate-200 focus:border-brand-blue"
                  data-testid="search-name-input"
                />
              </div>
            </div>

            {/* Filter Toggle Button */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`${showFilters ? 'bg-brand-blue/10 border-brand-blue text-brand-blue' : 'border-slate-200'}`}
              data-testid="toggle-filters-button"
            >
              <Filter size={16} className="mr-2" />
              Filters {hasActiveFilters && <span className="ml-1 px-1.5 py-0.5 bg-brand-cyan text-white text-xs rounded-full">{[filterSkill, filterLocation, filterMinExp, filterMaxExp].filter(Boolean).length}</span>}
            </Button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-slate-500 hover:text-red-500"
                data-testid="clear-filters-button"
              >
                <RotateCcw size={16} className="mr-1" />
                Clear
              </Button>
            )}

            {/* Results Count */}
            <div className="ml-auto text-sm text-slate-500">
              Showing <span className="font-bold text-brand-blue">{filteredResumes.length}</span> of {resumes.length} resumes
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Filter by Skill */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Filter by Skill</label>
                <Input
                  placeholder="e.g., Python, Excel..."
                  value={filterSkill}
                  onChange={(e) => setFilterSkill(e.target.value)}
                  className="border-slate-200 focus:border-brand-cyan"
                  data-testid="filter-skill-input"
                />
              </div>

              {/* Filter by Location */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Filter by Location</label>
                <Select value={filterLocation || "all"} onValueChange={(val) => setFilterLocation(val === "all" ? "" : val)}>
                  <SelectTrigger className="border-slate-200">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {filterOptions.locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Min Experience */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Min Experience (years)</label>
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={filterMinExp}
                  onChange={(e) => setFilterMinExp(e.target.value)}
                  className="border-slate-200 focus:border-brand-orange"
                  data-testid="filter-min-exp-input"
                />
              </div>

              {/* Max Experience */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Max Experience (years)</label>
                <Input
                  type="number"
                  min="0"
                  placeholder="50"
                  value={filterMaxExp}
                  onChange={(e) => setFilterMaxExp(e.target.value)}
                  className="border-slate-200 focus:border-brand-orange"
                  data-testid="filter-max-exp-input"
                />
              </div>
            </div>
          )}
        </Card>

        {matchResults && (
          <Card className="bg-white/90 backdrop-blur-sm border-2 border-brand-cyan/20 p-6 mb-6 shadow-lg rounded-xl" data-testid="match-results-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-magenta to-pink-400 flex items-center justify-center">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold font-heading text-slate-900">Matching Jobs Found</h2>
                  <p className="text-sm text-slate-500">Jobs that match your uploaded resumes</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setMatchResults(null)} className="hover:bg-red-50 hover:text-red-500" data-testid="close-match-results">
                <X size={20} />
              </Button>
            </div>
            
            <div className="space-y-4">
              {matchResults.map((result, idx) => (
                <div key={idx} className="border-2 border-slate-100 rounded-xl p-4 hover:border-brand-magenta/30 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        idx === 0 ? 'bg-brand-cyan' : 
                        idx === 1 ? 'bg-brand-orange' : 
                        'bg-brand-magenta'
                      }`} />
                      <h3 className="font-bold text-slate-900">{result.job.title}</h3>
                      <StatusBadge status={result.job.status} />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/jobs/${result.job.id}`, '_blank')}
                      className="border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan hover:text-white"
                      data-testid={`view-job-${result.job.id}`}
                    >
                      <ExternalLink size={14} className="mr-1" />
                      View Details
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {result.matches.map((match) => (
                      <div key={match.resume_id} className="bg-gradient-to-r from-slate-50 to-brand-magenta/5 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-slate-900">{match.resume_name}</p>
                          <span className={`font-mono font-bold ${
                            match.total_score >= 80 ? 'text-green-600' :
                            match.total_score >= 60 ? 'text-brand-yellow' :
                            'text-brand-orange'
                          }`}>
                            {match.total_score.toFixed(1)}%
                          </span>
                        </div>
                        <ScoreBar score={match.total_score} category={match.category} />
                        <p className="text-xs text-slate-600 mt-2">
                          <span className="font-medium text-brand-blue">{match.category}</span> • 
                          Skills: <span className="text-brand-cyan">{match.skill_score.toFixed(1)}%</span> • 
                          Experience: <span className="text-brand-orange">{match.experience_score.toFixed(1)}%</span> • 
                          Tools: <span className="text-brand-magenta">{match.tools_score.toFixed(1)}%</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3 text-slate-500">
              <div className="w-6 h-6 border-2 border-brand-cyan border-t-transparent rounded-full animate-spin"></div>
              Loading resumes...
            </div>
          </div>
        ) : resumes.length === 0 ? (
          <Card className="bg-white/80 backdrop-blur-sm border-2 border-brand-cyan/20 p-12 text-center rounded-xl" data-testid="empty-resumes-state">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-brand-cyan/20 to-brand-blue/20 rounded-2xl flex items-center justify-center mb-4">
              <Users size={40} className="text-brand-cyan" />
            </div>
            <p className="text-slate-600 mb-4 font-medium">No resumes uploaded yet</p>
            <Button
              onClick={() => document.getElementById('file-upload-empty').click()}
              className="bg-gradient-to-r from-brand-blue to-brand-cyan text-white hover:from-brand-cyan hover:to-brand-blue"
              data-testid="upload-first-resume-button"
            >
              Upload Your First Resume
            </Button>
            <input
              id="file-upload-empty"
              type="file"
              multiple
              accept=".pdf,.docx,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
          </Card>
        ) : filteredResumes.length === 0 ? (
          <Card className="bg-white/80 backdrop-blur-sm border-2 border-amber-200 p-12 text-center rounded-xl" data-testid="no-filter-results">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center mb-4">
              <Search size={40} className="text-amber-500" />
            </div>
            <p className="text-slate-600 mb-2 font-medium">No resumes match your filters</p>
            <p className="text-sm text-slate-500 mb-4">Try adjusting your search criteria</p>
            <Button
              onClick={clearFilters}
              variant="outline"
              className="border-amber-300 text-amber-600 hover:bg-amber-50"
            >
              <RotateCcw size={16} className="mr-2" />
              Clear All Filters
            </Button>
          </Card>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm border-2 border-slate-100 rounded-xl shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[1200px]" data-testid="resumes-table">
                <thead className="bg-gradient-to-r from-brand-blue/5 to-brand-cyan/5 text-slate-600 font-medium sticky top-0">
                  <tr>
                    <th className="px-4 py-4 border-b border-slate-100 whitespace-nowrap">Candidate Name</th>
                    <th className="px-4 py-4 border-b border-slate-100 whitespace-nowrap">Contact</th>
                    <th className="px-4 py-4 border-b border-slate-100 whitespace-nowrap">Location</th>
                    <th className="px-4 py-4 border-b border-slate-100 whitespace-nowrap">Salary</th>
                    <th className="px-4 py-4 border-b border-slate-100 whitespace-nowrap">Skills</th>
                    <th className="px-4 py-4 border-b border-slate-100 text-center whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <span>Overall</span>
                        <span className="text-xs text-brand-blue">Experience</span>
                      </div>
                    </th>
                    <th className="px-4 py-4 border-b border-slate-100 whitespace-nowrap">Upload Date</th>
                    <th className="px-4 py-4 border-b border-slate-100 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
              <tbody>
                {filteredResumes.map((resume, index) => (
                  <React.Fragment key={resume.id}>
                    <tr
                      className="border-b border-slate-50 hover:bg-gradient-to-r hover:from-white hover:to-brand-cyan/5 transition-all"
                      data-testid={`resume-row-${resume.id}`}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleMatchingJobs(resume.id)}
                            className="text-slate-400 hover:text-brand-cyan transition-colors"
                          >
                            {expandedMatches[resume.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                          <div className={`w-2 h-2 rounded-full ${
                            index % 4 === 0 ? 'bg-brand-blue' : 
                            index % 4 === 1 ? 'bg-brand-cyan' : 
                            index % 4 === 2 ? 'bg-brand-orange' : 
                            'bg-brand-magenta'
                          }`} />
                          <div>
                            <p className="font-medium text-slate-900">{resume.name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{resume.filename}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          {resume.email && (
                            <div className="flex items-center gap-1 text-xs text-slate-600">
                              <Mail size={12} className="text-brand-blue" />
                              <span className="truncate max-w-[150px]">{resume.email}</span>
                            </div>
                          )}
                          {resume.phone && (
                            <div className="flex items-center gap-1 text-xs text-slate-600">
                              <Phone size={12} className="text-brand-cyan" />
                              {resume.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          {(resume.current_location || resume.location) && (
                            <div className="flex items-center gap-1 text-xs">
                              <MapPin size={12} className="text-brand-orange" />
                              <span className="text-slate-600">{resume.current_location || resume.location}</span>
                            </div>
                          )}
                          {resume.preferred_locations && resume.preferred_locations.length > 0 && (
                            <div className="text-xs text-slate-500">
                              <span className="text-brand-magenta font-medium">Preferred:</span> {resume.preferred_locations.slice(0, 2).join(', ')}
                              {resume.preferred_locations.length > 2 && ` +${resume.preferred_locations.length - 2}`}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1 text-xs">
                          {resume.current_salary && (
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Current:</span>
                              <span className="text-slate-700 font-medium">{resume.current_salary}</span>
                            </div>
                          )}
                          {resume.expected_salary && (
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Expected:</span>
                              <span className="text-brand-cyan font-medium">{resume.expected_salary}</span>
                            </div>
                          )}
                          {!resume.current_salary && !resume.expected_salary && (
                            <span className="text-slate-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(expandedSkills[resume.id] ? resume.skills : resume.skills.slice(0, 3)).map((skill, idx) => (
                            <span
                              key={idx}
                              className={`px-2 py-0.5 text-xs rounded-full ${
                                idx % 3 === 0 ? 'bg-brand-blue/10 text-brand-blue' : 
                                idx % 3 === 1 ? 'bg-brand-cyan/10 text-brand-cyan' : 
                                'bg-brand-orange/10 text-brand-orange'
                              }`}
                            >
                              {skill}
                            </span>
                          ))}
                          {resume.skills.length > 3 && (
                            <button
                              onClick={() => toggleSkills(resume.id)}
                              className="px-2 py-0.5 bg-brand-magenta/10 text-brand-magenta text-xs rounded-full hover:bg-brand-magenta/20 transition-colors font-medium"
                            >
                              {expandedSkills[resume.id] ? 'Show Less' : `+${resume.skills.length - 3} more`}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col items-center">
                          <span className="font-mono text-lg font-bold text-brand-blue">
                            {resume.experience_years ? `${resume.experience_years}` : '0'}
                          </span>
                          <span className="text-[10px] text-slate-500 font-medium">years</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                          <Calendar size={12} />
                          {new Date(resume.upload_date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewResume(resume.id)}
                            data-testid={`view-resume-${resume.id}`}
                            title="View Resume"
                            className="hover:bg-brand-blue/10"
                          >
                            <Eye size={16} className="text-brand-blue" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadResume(resume)}
                            data-testid={`download-resume-${resume.id}`}
                            title="Download Resume"
                            className="hover:bg-green-50"
                          >
                            <Download size={16} className="text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(resume.id)}
                            data-testid={`delete-resume-${resume.id}`}
                            title="Delete Resume"
                            className="hover:bg-red-50"
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedMatches[resume.id] && (
                      <tr className="bg-gradient-to-r from-brand-cyan/5 to-brand-magenta/5">
                        <td colSpan="8" className="px-4 py-4">
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-cyan to-brand-blue flex items-center justify-center">
                              <Briefcase size={14} className="text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-slate-900 mb-3">Matching Job Descriptions (Score ≥ 40%)</p>
                              {loadingMatches[resume.id] ? (
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <div className="w-4 h-4 border-2 border-brand-cyan border-t-transparent rounded-full animate-spin"></div>
                                  Loading matches...
                                </div>
                              ) : expandedMatches[resume.id].length === 0 ? (
                                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                                  <p className="text-sm text-amber-800 font-medium mb-2">No matches found (score ≥ 40%)</p>
                                  <p className="text-xs text-amber-700">
                                    Consider adding more relevant skills or updating your resume to match active job requirements.
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {expandedMatches[resume.id].map((match, idx) => (
                                    <div
                                      key={idx}
                                      className="bg-white border-2 border-slate-100 rounded-xl p-3 hover:border-brand-cyan/30 transition-all"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                          idx % 3 === 0 ? 'bg-brand-cyan' : 
                                          idx % 3 === 1 ? 'bg-brand-orange' : 
                                          'bg-brand-magenta'
                                        }`} />
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <p className="font-semibold text-slate-900 text-sm truncate max-w-[200px]">{match.jd_title}</p>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                                              match.total_score >= 80 ? 'bg-green-100 text-green-700' :
                                              match.total_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                              match.total_score >= 50 ? 'bg-orange-100 text-orange-700' :
                                              'bg-red-100 text-red-700'
                                            }`}>
                                              {match.category}
                                            </span>
                                          </div>
                                          <p className="text-[10px] text-slate-500 mt-0.5">
                                            Skills: {match.skill_score}% • Exp: {match.experience_score}%
                                          </p>
                                        </div>
                                        <span className={`font-mono text-lg font-bold flex-shrink-0 ${
                                          match.total_score >= 80 ? 'text-green-600' :
                                          match.total_score >= 60 ? 'text-yellow-600' :
                                          match.total_score >= 50 ? 'text-orange-600' :
                                          'text-red-600'
                                        }`}>
                                          {match.total_score}%
                                        </span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => window.open(`/jobs/${match.jd_id}`, '_blank')}
                                          className="border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan hover:text-white text-xs px-2 py-1 h-7 flex-shrink-0"
                                        >
                                          <ExternalLink size={12} className="mr-1" />
                                          View
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resume: {resumeContent?.name || 'Unknown'}</DialogTitle>
          </DialogHeader>
          {resumeContent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">File Name</p>
                  <p className="font-semibold">{resumeContent.filename}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Candidate Name</p>
                  <p className="font-semibold">{resumeContent.name || 'N/A'}</p>
                </div>
              </div>

              {resumeContent.parsed_data && (
                <div className="border border-slate-200 rounded-lg p-4">
                  <h3 className="font-bold text-slate-900 mb-3">Parsed Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {resumeContent.parsed_data.email && (
                      <div>
                        <p className="text-slate-500">Email</p>
                        <p className="font-medium">{resumeContent.parsed_data.email}</p>
                      </div>
                    )}
                    {resumeContent.parsed_data.phone && (
                      <div>
                        <p className="text-slate-500">Phone</p>
                        <p className="font-medium">{resumeContent.parsed_data.phone}</p>
                      </div>
                    )}
                    {resumeContent.parsed_data.location && (
                      <div>
                        <p className="text-slate-500">Location</p>
                        <p className="font-medium">{resumeContent.parsed_data.location}</p>
                      </div>
                    )}
                    {resumeContent.parsed_data.experience_years && (
                      <div>
                        <p className="text-slate-500">Experience</p>
                        <p className="font-medium">{resumeContent.parsed_data.experience_years} years</p>
                      </div>
                    )}
                    {resumeContent.parsed_data.education && (
                      <div className="col-span-2">
                        <p className="text-slate-500">Education</p>
                        <p className="font-medium">{resumeContent.parsed_data.education}</p>
                      </div>
                    )}
                    {resumeContent.parsed_data.skills && resumeContent.parsed_data.skills.length > 0 && (
                      <div className="col-span-2">
                        <p className="text-slate-500 mb-2">Skills</p>
                        <div className="flex flex-wrap gap-2">
                          {resumeContent.parsed_data.skills.map((skill, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {resumeContent.parsed_data.tools && resumeContent.parsed_data.tools.length > 0 && (
                      <div className="col-span-2">
                        <p className="text-slate-500 mb-2">Tools & Technologies</p>
                        <div className="flex flex-wrap gap-2">
                          {resumeContent.parsed_data.tools.map((tool, idx) => (
                            <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                              {tool}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="font-bold text-slate-900 mb-3">Full Resume Text</h3>
                <div className="bg-slate-50 p-4 rounded text-sm whitespace-pre-wrap font-mono text-slate-700 max-h-96 overflow-y-auto">
                  {resumeContent.raw_text || 'No text content available'}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Match Results Dialog */}
      <Dialog open={!!uploadMatchResults} onOpenChange={() => setUploadMatchResults(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-blue flex items-center justify-center">
                <Sparkles size={20} className="text-white" />
              </div>
              <div>
                <span className="text-xl">Matching Jobs Found!</span>
                <p className="text-sm font-normal text-slate-500">Jobs that match your uploaded resume(s)</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {uploadMatchResults && (
            <div className="space-y-6 mt-4">
              {uploadMatchResults.map((result, idx) => (
                <div key={idx} className="border-2 border-slate-100 rounded-xl p-4 hover:border-brand-cyan/30 transition-colors">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100">
                    <div className={`w-3 h-3 rounded-full ${
                      idx % 3 === 0 ? 'bg-brand-blue' : 
                      idx % 3 === 1 ? 'bg-brand-cyan' : 
                      'bg-brand-magenta'
                    }`} />
                    <div>
                      <h3 className="font-bold text-slate-900">{result.resume_name}</h3>
                      <p className="text-sm text-slate-500">{result.matches.length} matching job(s)</p>
                    </div>
                  </div>
                  
                  {result.matches.length === 0 ? (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                      <p className="text-sm text-amber-800 font-medium">No matches found (score ≥ 40%)</p>
                      <p className="text-xs text-amber-700 mt-1">This resume may need more relevant skills or experience for current job openings.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {result.matches.slice(0, 10).map((match, matchIdx) => (
                        <div key={matchIdx} className="bg-gradient-to-r from-slate-50 to-brand-cyan/5 rounded-xl p-4 border border-slate-100">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Briefcase size={16} className="text-brand-blue flex-shrink-0" />
                                <p className="font-semibold text-slate-900 truncate">{match.jd_title}</p>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                                  match.total_score >= 80 ? 'bg-green-100 text-green-700' :
                                  match.total_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  match.total_score >= 50 ? 'bg-orange-100 text-orange-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {match.category}
                                </span>
                              </div>
                              <div className="max-w-md">
                                <ScoreBar score={match.total_score} category={match.category} />
                              </div>
                              <p className="text-xs text-slate-600 mt-2">
                                Skills: <span className="text-brand-cyan font-medium">{match.skill_score}%</span> • 
                                Exp: <span className="text-brand-orange font-medium">{match.experience_score}%</span>
                              </p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className={`font-mono text-2xl font-bold ${
                                match.total_score >= 80 ? 'text-green-600' :
                                match.total_score >= 60 ? 'text-yellow-600' :
                                match.total_score >= 50 ? 'text-orange-600' :
                                'text-red-600'
                              }`}>
                                {match.total_score}%
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`/jobs/${match.jd_id}`, '_blank')}
                                className="border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan hover:text-white"
                              >
                                <ExternalLink size={14} className="mr-1" />
                                View
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {result.matches.length > 10 && (
                        <p className="text-sm text-slate-500 text-center pt-2">
                          +{result.matches.length - 10} more matching jobs
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Resumes;